/* eslint-disable */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Snackbar,
  useMediaQuery,
  Fade,
  Zoom,
  Stack
} from '@mui/material';
import {
  Mic,
  Stop,
  Send,
  ArrowBack,
  Edit,
  RestartAlt,
  ExpandMore,
  InfoOutlined,
  Lightbulb,
  PlayArrow,
  Pause,
  VolumeUp,
  Psychology,
  RecordVoiceOver,
  Description,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import CVPreview from '../components/CVPreview';
import { generateCV } from '../services/cvService';

// Constants
const SUPPORTED_AUDIO_TYPES = ['audio/mp4', 'audio/webm', 'audio/wav'];
const MAX_RECORDING_TIME = 600; // 10 minutes in seconds
const TRANSCRIPT_MIN_LENGTH = 50; // Minimum characters for a meaningful CV

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

// Memoized components
const LoadingSpinner = React.memo(({ message, progress }) => (
  <Box sx={{ textAlign: 'center', width: '100%' }}>
    <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
    <Typography variant="h6" gutterBottom>{message}</Typography>
    {progress !== undefined && (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress variant="determinate" value={progress} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {Math.round(progress)}% complete
        </Typography>
      </Box>
    )}
  </Box>
));

const RecordingButton = React.memo(({ isRecording, recordingTime, onStart, onStop, disabled }) => {
  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }, []);

  const progressValue = (recordingTime / MAX_RECORDING_TIME) * 100;

  if (isRecording) {
    return (
      <Box sx={{ textAlign: 'center', width: '100%' }}>
        <Box 
          component={motion.div}
          animate={{ 
            scale: [1, 1.1, 1],
            backgroundColor: ['#f44336', '#e57373', '#f44336']
          }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          sx={{ 
            width: 120, 
            height: 120, 
            borderRadius: '50%', 
            bgcolor: 'error.main', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 3,
            mx: 'auto',
            position: 'relative'
          }}
        >
          <Stop sx={{ color: 'white', fontSize: 60 }} />
          <CircularProgress
            variant="determinate"
            value={progressValue}
            size={130}
            thickness={3}
            sx={{
              position: 'absolute',
              color: 'white',
              opacity: 0.7
            }}
          />
        </Box>
        <Typography variant="h5" sx={{ mb: 2, color: 'error.main' }}>
          <RecordVoiceOver sx={{ mr: 1, verticalAlign: 'middle' }} />
          Recording... {formatTime(recordingTime)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {recordingTime > MAX_RECORDING_TIME * 0.8 && 
            `${formatTime(MAX_RECORDING_TIME - recordingTime)} remaining`}
        </Typography>
        <Button 
          variant="contained" 
          color="error" 
          size="large"
          startIcon={<Stop />}
          onClick={onStop}
          sx={{ px: 4, py: 1.5 }}
        >
          Stop Recording
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', width: '100%' }}>
      <Box 
        component={motion.div}
        whileHover={{ scale: disabled ? 1 : 1.05 }}
        whileTap={{ scale: disabled ? 1 : 0.95 }}
        sx={{ 
          width: 120, 
          height: 120, 
          borderRadius: '50%', 
          bgcolor: disabled ? 'grey.400' : 'primary.main', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 3,
          mx: 'auto',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease'
        }}
        onClick={disabled ? undefined : onStart}
      >
        <Mic sx={{ color: 'white', fontSize: 60 }} />
      </Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {disabled ? 'Processing...' : 'Click to Start Recording'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Speak clearly about your professional experience
      </Typography>
      <Button 
        variant="contained" 
        color="primary" 
        size="large"
        startIcon={<Mic />}
        onClick={onStart}
        disabled={disabled}
        sx={{ px: 4, py: 1.5 }}
      >
        Start Voice Recording
      </Button>
    </Box>
  );
});

const AudioPlayer = React.memo(({ audioBlob, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current = new Audio(audioUrl);
      
      const audio = audioRef.current;
      audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
      audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
      audio.addEventListener('ended', () => setIsPlaying(false));

      return () => {
        audio.pause();
        URL.revokeObjectURL(audioUrl);
      };
    }
  }, [audioBlob]);

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  if (!audioBlob) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={togglePlayback} color="primary">
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={duration ? (currentTime / duration) * 100 : 0}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Box>
          <VolumeUp color="action" />
          <Button size="small" onClick={onClose}>
            Remove
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
});

const TranscriptStats = React.memo(({ transcript }) => {
  const wordCount = transcript.trim().split(/\s+/).filter(word => word.length > 0).length;
  const charCount = transcript.length;
  const isMinimumMet = charCount >= TRANSCRIPT_MIN_LENGTH;

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <Chip 
        icon={<Description />}
        label={`${charCount} characters`}
        color={isMinimumMet ? 'success' : 'warning'}
        size="small"
      />
      <Chip 
        icon={<Psychology />}
        label={`${wordCount} words`}
        color="primary"
        size="small"
      />
      {!isMinimumMet && (
        <Chip 
          icon={<Warning />}
          label={`Need ${TRANSCRIPT_MIN_LENGTH - charCount} more chars`}
          color="warning"
          size="small"
        />
      )}
    </Box>
  );
});

const VoicePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State management
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const [cvMarkdown, setCvMarkdown] = useState('');
  const [loading, setLoading] = useState(false);
  const [formTemplate, setFormTemplate] = useState('modern');
  const [userStatus, setUserStatus] = useState({ isPremium: false });
  const [audioBlob, setAudioBlob] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Memoized values
  const isDevelopment = useMemo(() => process.env.NODE_ENV === 'development', []);
  
  // Utility functions
  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const logError = useCallback((message, error) => {
    if (isDevelopment) {
      console.error(message, error);
    }
  }, [isDevelopment]);

  const getAudioMimeType = useCallback(() => {
    for (const type of SUPPORTED_AUDIO_TYPES) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return 'audio/webm';
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError('');
      audioChunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      
      const mimeType = getAudioMimeType();
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000
      });
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        processAudioData();
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime((time) => {
          if (time >= MAX_RECORDING_TIME) {
            stopRecording();
            return time;
          }
          return time + 1;
        });
      }, 1000);
      
      showSnackbar('Recording started successfully!');
      
    } catch (err) {
      logError('Error accessing microphone:', err);
      setError(`Could not access microphone: ${err.message}`);
    }
  }, [getAudioMimeType, logError, showSnackbar]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
      
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      showSnackbar('Recording stopped. Processing audio...');
    }
  }, [isRecording, showSnackbar]);

  const processAudioData = useCallback(async () => {
    try {
      setIsProcessing(true);
      setProcessingProgress(0);
      
      const mimeType = getAudioMimeType();
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
      setAudioBlob(audioBlob);
      
      const audioFile = new File([audioBlob], 'recording.mp4', { type: mimeType });
      
      setProcessingProgress(25);
      
      try {
        const { default: OpenAI } = await import('openai');
        setProcessingProgress(50);
        
        const openai = new OpenAI({
          apiKey: process.env.REACT_APP_OPENAI_API_KEY,
          dangerouslyAllowBrowser: true
        });

        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('model', 'whisper-1');
        formData.append('response_format', 'text');
        
        setProcessingProgress(75);
        
        const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`OpenAI API error: ${JSON.stringify(errorData)}`);
        }

        const data = await response.json();
        setTranscript(data.text || data);
        setProcessingProgress(100);
        showSnackbar('Audio transcribed successfully!');
        
      } catch (err) {
        logError('Error transcribing audio:', err);
        setError(`Transcription failed: ${err.message}`);
      }
      
    } catch (err) {
      logError('Error processing audio:', err);
      setError(`Processing failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  }, [getAudioMimeType, logError, showSnackbar]);

  const handleGenerateCV = useCallback(async () => {
    if (!transcript.trim()) {
      setError('Please record or type your CV information first.');
      return;
    }

    if (transcript.length < TRANSCRIPT_MIN_LENGTH) {
      setError(`Please provide more detailed information. Minimum ${TRANSCRIPT_MIN_LENGTH} characters required.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = {
        personalInfo: {
          firstName: 'From',
          lastName: 'Voice',
          email: 'email@example.com',
          phone: '',
          location: '',
          title: 'Professional',
          summary: '',
        },
        experience: transcript,
        education: '',
        skills: '',
        certifications: '',
        languages: '',
        projects: '',
        memberships: '',
        references: '',
        template: formTemplate,
        fullTranscript: transcript
      };

      const data = await generateCV(formData);
      setCvMarkdown(data.cv);
      showSnackbar('CV generated successfully!');
    } catch (err) {
      logError('Error generating CV:', err);
      setError(err.message || 'Failed to generate CV. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [transcript, formTemplate, logError, showSnackbar]);

  const handleReset = useCallback(() => {
    setTranscript('');
    setCvMarkdown('');
    setRecordingTime(0);
    setError('');
    setAudioBlob(null);
    showSnackbar('Content reset successfully');
  }, [showSnackbar]);

  // Effects
  useEffect(() => {
    const storedPremiumStatus = localStorage.getItem('userPremiumStatus');
    if (storedPremiumStatus === 'true') {
      setUserStatus(prev => ({ ...prev, isPremium: true }));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current?.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Memoized components
  const tipsAccordion = useMemo(() => (
    <motion.div variants={itemVariants}>
      <Accordion 
        defaultExpanded={!isMobile}
        sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Lightbulb sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6">Tips for Best Results</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography paragraph>
            For the best CV generation, structure your speech with clear sections:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography component="div">
                <strong>Essential Information:</strong>
                <ul>
                  <li>Full name and contact details</li>
                  <li>Professional title and location</li>
                  <li>Brief professional summary</li>
                  <li>Work experience with dates</li>
                </ul>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography component="div">
                <strong>Additional Details:</strong>
                <ul>
                  <li>Education and qualifications</li>
                  <li>Technical and soft skills</li>
                  <li>Certifications and languages</li>
                  <li>Notable projects or achievements</li>
                </ul>
              </Typography>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography>
              <strong>Pro tip:</strong> Speak clearly at a moderate pace. The AI can only include what you tell it, so be comprehensive!
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>
    </motion.div>
  ), [isMobile]);

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 4, md: 8 },
        background: 'linear-gradient(135deg, #f5f7fa, #e4e9f0)',
        minHeight: '100vh'
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 1 }}>
            <Button 
              startIcon={<ArrowBack />} 
              onClick={() => navigate('/')}
              sx={{ mr: 2 }}
            >
              Back to Selection
            </Button>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1">
              Create Your CV with Voice
            </Typography>
            <Chip 
              icon={<CheckCircle />}
              label="AI-Powered"
              color="success"
              size="small"
              sx={{ ml: 'auto' }}
            />
          </Box>
        </motion.div>

        {tipsAccordion}

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <motion.div variants={itemVariants}>
              <Paper 
                sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Typography variant="h5" gutterBottom>
                  Record Your CV Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                <Box 
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    flexGrow: 1
                  }}
                >
                  {isProcessing ? (
                    <LoadingSpinner 
                      message="Transcribing your recording..." 
                      progress={processingProgress}
                    />
                  ) : (
                    <RecordingButton
                      isRecording={isRecording}
                      recordingTime={recordingTime}
                      onStart={startRecording}
                      onStop={stopRecording}
                      disabled={isProcessing}
                    />
                  )}
                </Box>

                <AudioPlayer 
                  audioBlob={audioBlob}
                  onClose={() => setAudioBlob(null)}
                />
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div variants={itemVariants}>
              <Paper 
                sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">
                    Edit Transcript
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Reset transcript">
                      <IconButton onClick={handleReset} disabled={!transcript}>
                        <RestartAlt />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit mode">
                      <IconButton>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                {transcript && <TranscriptStats transcript={transcript} />}
                
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 8 : 12}
                  variant="outlined"
                  placeholder="Your transcribed CV information will appear here. You can edit it before generating your CV."
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  sx={{ mb: 3, flexGrow: 1 }}
                  inputProps={{
                    'aria-label': 'CV transcript text'
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<Send />}
                    onClick={handleGenerateCV}
                    disabled={!transcript.trim() || loading || transcript.length < TRANSCRIPT_MIN_LENGTH}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    {loading ? 'Generating...' : 'Generate CV'}
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* CV Preview Section */}
        <AnimatePresence>
          {(loading || cvMarkdown) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Paper 
                sx={{ 
                  p: 4, 
                  mb: 4, 
                  borderRadius: 2, 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  mt: 4 
                }}
              >
                <Typography variant="h5" gutterBottom>
                  Your CV Preview
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <CVPreview 
                  cvMarkdown={cvMarkdown} 
                  loading={loading} 
                  template={formTemplate}
                  error={error}
                  onRetry={handleGenerateCV}
                  isPremiumUser={userStatus.isPremium}
                />
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Container>
  );
};

export default React.memo(VoicePage); 